import React, { useState, useContext, useEffect } from "react";
import "./Complaint.css";
import { useTranslation } from "react-i18next";
import { _t, getCookie } from "../../../../functions/Functions";
import { Helmet } from "react-helmet";
import ComplaintsSidebar from "./ComplaintsSidebar";
import { UserContext } from "../../../../contexts/User";
import { AuditContext } from "../../../../contexts/Audit";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import { ToastSuccess, ToastError } from "../../../../functions/ToastShow";
import Select from "react-select";
import { useParams, useHistory } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";

export default function ComplaintResolve() {
  const { t } = useTranslation();
  const { authUserInfo } = useContext(UserContext);
  const { id } = useParams();
  const history = useHistory();

  const { 
    loading,
    complaintResolutionTypes,
    complaintDepartments,
    complaintCauses,
    getComplaintResolutionTypes,
    getComplaintDepartments,
    getComplaintCauses
  } = useContext(AuditContext);

  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!complaintResolutionTypes) getComplaintResolutionTypes();
    if (!complaintDepartments) getComplaintDepartments();
    if (!complaintCauses) getComplaintCauses();
  }, []);
  
  const [formData, setFormData] = useState({
    resolution_type_ids: [],
    cause: null,
    ownedBy: null,
    action_next: "",
    guest_satisfied: "yes",
    total_cost: "0",
  });

  const updateForm = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (id) {
      fetchResolveData();
    }
  }, [id]);

  const fetchResolveData = async () => {
    setProcessing(true);
    try {
      const response = await axios.get(`${BASE_URL}/complaints/${id}/resolve`, {
        headers: { Authorization: `Bearer ${getCookie()}` }
      });
      const resData = response.data?.data || response.data;
      if (resData) {
        setFormData(prev => ({
          ...prev,
          resolution_type_ids: resData.resolution_type_ids || resData.resolution_type_id || [],
          action_next: resData.action_next || "",
          guest_satisfied: resData.guest_satisfied || "yes",
          total_cost: resData.total_cost || "0",
          cause: resData.cause_id ? { id: resData.cause_id, name: resData.cause_name } : null,
          ownedBy: resData.department_id ? { id: resData.department_id, name: resData.department_name } : null
        }));
      }
    } catch (error) {
      console.error("Error fetching resolve data", error);
    } finally {
      setProcessing(false);
    }
  };

  // Toggle resolution card selection
  const toggleResolution = (resId) => {
    if (formData.resolution_type_ids.includes(resId)) {
      updateForm("resolution_type_ids", formData.resolution_type_ids.filter((item) => item !== resId));
    } else {
      updateForm("resolution_type_ids", [...formData.resolution_type_ids, resId]);
    }
  };

  const submitResolve = async (status) => {
    if (!id) return;
    console.log("abcd4",status)
    if (!formData?.action_next.trim()) {
      ToastError({ title: _t(t("Please specify what we'll do next.")) });
      return;
    }
    if (!formData?.resolution_type_ids || formData.resolution_type_ids?.length === 0) {
      ToastError({ title: _t(t("Please select at least one resolution given.")) });
      return;
    }
    if (!formData.guest_satisfied) {
      ToastError({ title: _t(t("Please select if the guest is satisfied.")) });
      return;
    }
    setProcessing(true);
    
    const payload = {
      status: status,
      resolution_type_ids: formData.resolution_type_ids,
      cause_id: formData.cause ? formData.cause.id : null,
      department_id: formData.ownedBy ? formData.ownedBy.id : null,
      total_cost: parseFloat(formData.total_cost) || 0,
      action_next: formData.action_next,
      guest_satisfied: formData.guest_satisfied
    };

    try {
      await axios.post(`${BASE_URL}/complaints/${id}/resolve`, payload, {
        headers: { Authorization: `Bearer ${getCookie()}` }
      });
      ToastSuccess({ title: _t(t(`Complaint resolved successfully!`)) });
      history.push("/dashboard/complaints/tickets-list");
    } catch (error) {
      console.error("Error resolving complaint", error);
      ToastError({ title: _t(t("Failed to update complaint resolution.")) });
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => submitResolve("closed");
  const handleKeepOpen = () => submitResolve("in_progress");
  const handleReject = () => submitResolve("rejected");

  return (
    <>
      <Helmet>
        <title>{_t(t("Resolve & Close"))}</title>
      </Helmet>

      <main id="main" data-simplebar>

        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* Left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ComplaintsSidebar />
            </div>

            {/* Main Content Area */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <CustomSpinLoader loading={loading || processing || authUserInfo.permissions === null} />
              <div
                className="fk-scroll--pos-menu table-bottom-info-hide"
                data-simplebar
              >
                <div className="board-container">
                  {/* Top Header */}
                  <div className="board-header">
                    <div className="board-title">
                      <h1>{_t(t("Resolve & close"))}</h1>
                      <p>{_t(t("Manager – void, comp, refund, approval"))}</p>
                    </div>
                  </div>

                  {/* Full Layout */}
                  <div className="ticket-layout-full">
                      {/* 1. Resolution Given Card */}
                      <div className="ticket-card">
                        <div className="ticket-section-title">
                          {_t(t("Resolution given"))} <span className="text-danger">*</span>
                        </div>
                        <div className="ticket-section-subtext">
                          {_t(
                            t(
                              "Pick one or more. Anything that costs money posts straight to accounts – it isn't just noted here."
                            )
                          )}
                        </div>

                        {/* Resolution Options Grid */}
                        <div className="resolve-options-grid">
                          {complaintResolutionTypes && complaintResolutionTypes.map((opt) => (
                            <div
                              key={opt.id}
                              className={`resolve-option-tile ${
                                formData.resolution_type_ids.includes(opt.id) ? "active" : ""
                              }`}
                              onClick={() => toggleResolution(opt.id)}
                            >
                              <div className="resolve-option-title">{_t(t(opt.name))}</div>
                              <div className="resolve-option-desc">{_t(t(opt.desc || opt.description || ""))}</div>
                            </div>
                          ))}
                        </div>

                      </div>

                      {/* 2. Root Cause Card */}
                      <div className="ticket-card">
                        <div className="ticket-section-title">
                          {_t(t("Root cause — feedback to kitchen"))}
                        </div>
                        <div className="ticket-section-subtext">
                          {_t(
                            t(
                              "This field is what makes the report useful. Without it you only learn a complaint happened, not why."
                            )
                          )}
                        </div>

                        <div className="ticket-controls-row mb-3">
                          <div className="ticket-control-group">
                            <label className="ticket-control-label">{_t(t("Cause"))}</label>
                            <Select
                              classNamePrefix="select"
                              getOptionLabel={(option) => option.name || option.title}
                              getOptionValue={(option) => option.id}
                              options={complaintCauses || []}
                              value={formData.cause}
                              onChange={(selected) => updateForm("cause", selected)}
                              placeholder={_t(t("Select Cause"))}
                              isClearable
                            />
                          </div>

                          <div className="ticket-control-group">
                            <label className="ticket-control-label">{_t(t("Owned by"))}</label>
                            <Select
                              classNamePrefix="select"
                              getOptionLabel={(option) => option.name || option.title}
                              getOptionValue={(option) => option.id}
                              options={complaintDepartments || []}
                              value={formData.ownedBy}
                              onChange={(selected) => updateForm("ownedBy", selected)}
                              placeholder={_t(t("Select Department"))}
                              isClearable
                            />
                          </div>
                        </div>

                        <div className="ticket-control-group">
                          <label className="ticket-control-label">
                            {_t(t("What we'll do next"))} <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="c-input"
                            value={formData.action_next}
                            onChange={(e) => updateForm("action_next", e.target.value)}
                          />
                        </div>
                      </div>

                      {/* 3. Close Ticket Card */}
                      <div className="ticket-card">
                        <div className="ticket-section-title mb-3">
                          {_t(t("Close ticket"))}
                        </div>

                        <div className="ticket-controls-row mb-3">
                          <div className="ticket-control-group">
                            <label className="ticket-control-label">
                              {_t(t("Is the guest satisfied now?"))} <span className="text-danger">*</span>
                            </label>
                            <div className="btn-satisfaction-group">
                              {["yes", "somewhat", "no"].map((option) => (
                                <button
                                  key={option}
                                  type="button"
                                  className={`btn-satisfaction ${
                                    formData.guest_satisfied === option ? "active" : ""
                                  }`}
                                  onClick={() => updateForm("guest_satisfied", option)}
                                  style={{ textTransform: 'capitalize' }}
                                >
                                  {_t(t(option))}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="ticket-control-group">
                            <label className="ticket-control-label">{_t(t("Total cost"))}</label>
                            <input
                              type="number"
                              className="c-input"
                              value={formData.total_cost}
                              onChange={(e) => updateForm("total_cost", e.target.value)}
                            />
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="ticket-actions-row">
                          <button
                            type="button"
                            className="ticket-btn-add"
                            onClick={handleClose}
                          >
                            {_t(t("Close"))}
                          </button>
                          <button
                            type="button"
                            className="ticket-btn-escalate"
                            onClick={handleKeepOpen}
                          >
                            {_t(t("Keep open"))}
                          </button>
                          <button
                            type="button"
                            className="btn-reject"
                            onClick={handleReject}
                          >
                            {_t(t("Reject — not valid"))}
                          </button>
                        </div>

                        {/* Lock Warning Notice */}
                        <div className="ticket-lock-notice">
                          {_t(
                            t(
                              "Once closed, the ticket locks. Only a Branch Manager or Head Office can reopen it, and that reopen is itself logged."
                            )
                          )}
                        </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
